import type { AcademicTerm, HomeroomAffairChange, HomeroomRecord } from "@classroom/domain";
import { useRef, useState } from "react";
import { useTranslation } from "./i18n";
import { useDialogFocus } from "./lib/accessibility";
import { useSaveBeforeUnload } from "./lib/useSaveBeforeUnload";
import "./teacher-workflows.css";

type Entry = HomeroomRecord["affairs"][number] & { date: string };
export default function HomeroomNotebook({
  records,
  defaultDate,
  term,
  readOnly,
  onChange,
  toast,
}: {
  records: HomeroomRecord[];
  defaultDate: string;
  term?: AcademicTerm;
  readOnly: boolean;
  onChange: (date: string, change: HomeroomAffairChange) => Promise<unknown>;
  toast: (message: string) => void;
}) {
  const { t } = useTranslation("homeroomNotebook");
  const [date, setDate] = useState(defaultDate);
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<Entry>();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const pendingAdd = useRef<HomeroomAffairChange | undefined>(undefined);
  useSaveBeforeUnload(saving || Boolean(title || note));
  const entries = records
    .flatMap((record) => record.affairs.map((item) => ({ ...item, date: record.date })))
    .sort((a, b) => b.date.localeCompare(a.date));
  const visible = entries.filter((entry) =>
    `${entry.date} ${entry.title} ${entry.note || ""}`.toLowerCase().includes(search.trim().toLowerCase()),
  );
  async function add() {
    if (readOnly || saving || !title.trim()) return;
    const change = pendingAdd.current ?? {
      after: { id: `affair_${crypto.randomUUID()}`, title: title.trim(), note: note.trim() },
    };
    pendingAdd.current = change;
    setSaving(true);
    setError("");
    try {
      await onChange(date, change);
      setTitle("");
      setNote("");
      pendingAdd.current = undefined;
      toast(t("added"));
    } catch {
      setError(t("failed"));
    } finally {
      setSaving(false);
    }
  }
  return (
    <div
      className="homeroom-affairs-layout"
      data-autosave
      data-save-state={saving ? "saving" : title || note ? "dirty" : "saved"}
    >
      <section className="paper-card homeroom-affair-form">
        <h2>{t("addTitle")}</h2>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void add();
          }}
        >
          <label>
            {t("date")}
            <input
              aria-label={t("date")}
              type="date"
              required
              min={term?.startDate}
              max={term?.endDate}
              value={date}
              disabled={readOnly || saving}
              onChange={(event) => {
                setDate(event.target.value);
                pendingAdd.current = undefined;
                setError("");
              }}
            />
          </label>
          <label>
            {t("event")}
            <input
              aria-label={t("event")}
              name="title"
              required
              maxLength={200}
              value={title}
              placeholder={t("placeholder")}
              data-demo-action="homeroom-title"
              disabled={readOnly || saving}
              onChange={(event) => {
                setTitle(event.target.value);
                pendingAdd.current = undefined;
                setError("");
              }}
            />
          </label>
          <label>
            {t("note")}
            <textarea
              aria-label={t("note")}
              name="note"
              rows={4}
              maxLength={10000}
              value={note}
              data-demo-action="homeroom-note"
              disabled={readOnly || saving}
              onChange={(event) => {
                setNote(event.target.value);
                pendingAdd.current = undefined;
                setError("");
              }}
            />
          </label>
          {error && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}
          <button
            className="primary-action"
            data-demo-action="homeroom-save"
            disabled={readOnly || saving || !title.trim()}
          >
            {saving ? t("saving") : t("add")}
          </button>
        </form>
      </section>
      <section className="paper-card homeroom-notebook workflow-notebook">
        <header>
          <h2>{t("title")}</h2>
          <small>{entries.length}</small>
        </header>
        {entries.length > 0 && (
          <input
            type="search"
            aria-label={t("search")}
            placeholder={t("search")}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        )}
        {visible.map((entry) => (
          <article key={`${entry.date}:${entry.id}`}>
            <div className="workflow-entry-heading">
              <time>{entry.date}</time>
              <button
                type="button"
                aria-label={t("editLabel", { title: entry.title })}
                disabled={readOnly || saving}
                onClick={() => setEditing(entry)}
              >
                {t("edit")}
              </button>
            </div>
            <b>{entry.title}</b>
            <p>{entry.note || "—"}</p>
          </article>
        ))}
        {!visible.length && <p className="empty-copy">{t("empty")}</p>}
      </section>
      {editing && (
        <AffairEditor
          key={`${editing.date}:${editing.id}`}
          entry={editing}
          readOnly={readOnly}
          onClose={() => setEditing(undefined)}
          onChange={onChange}
          toast={toast}
        />
      )}
    </div>
  );
}
function AffairEditor({
  entry,
  readOnly,
  onClose,
  onChange,
  toast,
}: {
  entry: Entry;
  readOnly: boolean;
  onClose: () => void;
  onChange: (date: string, change: HomeroomAffairChange) => Promise<unknown>;
  toast: (message: string) => void;
}) {
  const { t } = useTranslation("homeroomNotebook");
  const [title, setTitle] = useState(entry.title),
    [note, setNote] = useState(entry.note || "");
  const [deleting, setDeleting] = useState(false),
    [saving, setSaving] = useState(false),
    [error, setError] = useState("");
  const dirty = title !== entry.title || note !== (entry.note || "");
  const close = () => {
    if (!saving && (!dirty || window.confirm(t("discard")))) onClose();
  };
  const ref = useDialogFocus(true, close);
  useSaveBeforeUnload(saving || dirty);
  async function save() {
    if (readOnly || saving) return;
    setSaving(true);
    setError("");
    const before = { id: entry.id, title: entry.title, note: entry.note };
    try {
      await onChange(entry.date, {
        before,
        after: deleting ? undefined : { id: entry.id, title: title.trim(), note: note.trim() },
      });
      toast(t(deleting ? "deleted" : "updated"));
      onClose();
    } catch (caught) {
      setError(t(caught instanceof Error && caught.message === "stale-affair" ? "stale" : "failed"));
    } finally {
      setSaving(false);
    }
  }
  return (
    <div className="assignment-folder-overlay">
      <div
        className="paper-card workflow-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={t(deleting ? "deleteTitle" : "editTitle")}
        tabIndex={-1}
        ref={ref}
        data-autosave
        data-save-state={saving ? "saving" : dirty ? "dirty" : "saved"}
      >
        <h3>{t(deleting ? "deleteTitle" : "editTitle")}</h3>
        {deleting ? (
          <p>{t("deleteMessage", { title: entry.title })}</p>
        ) : (
          <div className="workflow-fields">
            <span className="workflow-context">{entry.date}</span>
            <label>
              {t("event")}
              <input
                aria-label={t("event")}
                value={title}
                maxLength={200}
                required
                disabled={readOnly || saving}
                data-dialog-initial-focus
                onChange={(event) => setTitle(event.target.value)}
              />
            </label>
            <label>
              {t("note")}
              <textarea
                aria-label={t("note")}
                rows={6}
                value={note}
                maxLength={10000}
                disabled={readOnly || saving}
                onChange={(event) => setNote(event.target.value)}
              />
            </label>
          </div>
        )}
        {error && (
          <p role="alert" className="form-error">
            {error}
          </p>
        )}
        <div className="assignment-folder-dialog-actions">
          {!deleting && (
            <button
              type="button"
              className="assignment-folder-delete"
              disabled={readOnly || saving}
              onClick={() => setDeleting(true)}
            >
              {t("delete")}
            </button>
          )}
          <button type="button" disabled={saving} onClick={() => (deleting ? setDeleting(false) : close())}>
            {t(deleting ? "back" : "cancel")}
          </button>
          <button
            type="button"
            className={deleting ? "danger-action" : "primary-action"}
            disabled={readOnly || saving || (!deleting && (!title.trim() || !dirty))}
            onClick={() => void save()}
          >
            {saving ? t("saving") : t(deleting ? "confirmDelete" : "save")}
          </button>
        </div>
      </div>
    </div>
  );
}
